import { logger } from "../utils/logger";
import { useState, useEffect } from "react";
import axios from "axios";
import { MapPin, Phone, Clock, Facebook, Instagram } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const response = await axios.get(`${API}/contact`);
        setContact(response.data);
      } catch (err) {
        logger.error("Error fetching contact:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchContact();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-casino-black">
        <div className="text-gold animate-pulse font-heading text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-casino-black to-casino-dark relative overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <p className="text-gold uppercase tracking-[0.3em] text-sm mb-6 font-accent">
            Get in Touch
          </p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-white mb-6">
            Contact <span className="gold-gradient">Us</span>
          </h1>
          <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto">
            Have questions? We'd love to hear from you. Visit us or send us a message.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-20 bg-casino-black">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="font-heading text-3xl text-white mb-8">Visit Us</h2>
              
              <div className="space-y-8 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center shrink-0">
                    <MapPin className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">Address</h3>
                    <span className="text-white/40 italic">Coming Soon</span>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center shrink-0">
                    <Phone className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">Phone</h3>
                    <span className="text-white/40 italic">Coming Soon</span>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center shrink-0">
                    <Clock className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">Hours</h3>
                    <span className="text-white/40 italic">Coming Soon</span>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="text-white font-medium mb-4">Follow Us</h3>
                <div className="flex gap-4">
                  <a
                    href={contact?.facebook_url || "https://www.facebook.com/OTRSanMarcos/"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-white/60 hover:text-gold hover:bg-white/10 transition-all"
                    data-testid="contact-facebook"
                  >
                    <Facebook className="w-6 h-6" />
                  </a>
                  <a
                    href={contact?.instagram_url || "https://www.instagram.com/ontheriverpokerclubsm/"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-white/60 hover:text-gold hover:bg-white/10 transition-all"
                    data-testid="contact-instagram"
                  >
                    <Instagram className="w-6 h-6" />
                  </a>
                  <a
                    href={contact?.tiktok_url || "https://www.tiktok.com/@ontheriverpokerclubsm"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-white/60 hover:text-gold hover:bg-white/10 transition-all"
                    data-testid="contact-tiktok"
                  >
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </a>
                </div>
              </div>

              {/* Get Directions Button */}
              <div className="mt-8">
                <span className="inline-flex items-center gap-2 bg-white/10 text-white/40 italic px-6 py-3 text-sm cursor-default">
                  <MapPin className="w-4 h-4" />
                  Directions Coming Soon
                </span>
              </div>
            </div>

            {/* GoHighLevel Contact Form - Demo Mode */}
            <div className="glass-card p-8 md:p-10 flex flex-col items-center justify-center min-h-[400px]">
              <div className="text-center">
                <h2 className="font-heading text-2xl text-white mb-4">Contact Form</h2>
                <p className="text-white/40 italic text-lg">Coming Soon</p>
                <p className="text-white/30 text-sm mt-2">This is a demo website. Contact form will be available when the site goes live.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section - Placeholder */}
      <section className="py-20 bg-casino-dark" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="aspect-[21/9] rounded-2xl overflow-hidden border border-white/10 bg-white/5 flex items-center justify-center">
            <p className="text-white/30 italic text-lg">Map Coming Soon</p>
          </div>
        </div>
      </section>
    </div>
  );
}
